import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type OnChangeFn,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> {
  /** Column definitions — use createColumnHelper or ColumnDef[] */
  columns: ColumnDef<TData, unknown>[];
  /** Data rows */
  data: TData[];
  /** Show skeleton loading state */
  isLoading?: boolean;
  /** Number of skeleton rows to show while loading */
  skeletonRows?: number;
  /** Server-side sorting state */
  sorting?: SortingState;
  /** Server-side sorting change handler */
  onSortingChange?: OnChangeFn<SortingState>;
  /** Callback when a row is clicked */
  onRowClick?: (row: TData) => void;
  /** Message shown when data is empty */
  emptyMessage?: string;
  /** Table density: 'compact' for dense admin tables, 'default' for standard */
  density?: 'compact' | 'default';
  /** Sticky header (useful for tall tables) */
  stickyHeader?: boolean;
  /**
   * Constrain the scroll container's height (any CSS length, e.g.
   * 'calc(100svh - 18rem)'). Keeps the body scrolling *inside* the table so the
   * horizontal scrollbar stays reachable instead of sinking to the page bottom.
   * Pairs naturally with `stickyHeader`.
   */
  maxHeight?: string;
  /**
   * Show a fade + chevron affordance on either horizontal edge when the table
   * overflows, signaling that more columns exist off-screen.
   */
  scrollHint?: boolean;
  /** Dynamic class/style per row */
  rowStyle?: (row: TData) => React.CSSProperties | undefined;
  /**
   * Column ids to freeze against the right edge, so they stay visible however far
   * the table is scrolled horizontally. Order here doesn't matter — offsets are
   * derived from where the columns actually sit in the table.
   *
   * Each listed column MUST declare an explicit `size` on its ColumnDef: offsets
   * are computed by summing the sizes of the pinned columns to its right, not by
   * measuring the DOM. A pinned column without a `size` falls back to TanStack's
   * 150px default, which will misalign the stack.
   */
  pinnedRightColumns?: string[];
}

export function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  skeletonRows = 8,
  sorting,
  onSortingChange,
  onRowClick,
  emptyMessage = 'No data found.',
  density = 'default',
  stickyHeader = false,
  maxHeight,
  scrollHint = false,
  rowStyle,
  pinnedRightColumns,
}: DataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: sorting ? { sorting } : undefined,
    onSortingChange,
  });

  const leafColumns = table.getAllLeafColumns();
  // Right offset per pinned column: the total width of the pinned columns to its
  // right. Walking right-to-left accumulates exactly that. The rightmost pinned
  // column lands at 0, and the first one in the group is flagged so it can carry
  // the divider marking where the frozen region begins.
  const pinnedOffsets = useMemo(() => {
    const offsets = new Map<string, { right: number; width: number; isEdge: boolean }>();
    if (!pinnedRightColumns?.length) return offsets;

    const pinnedIds = new Set(pinnedRightColumns);
    const pinnedInTableOrder = leafColumns.filter((column) => pinnedIds.has(column.id));

    let accumulated = 0;
    for (let i = pinnedInTableOrder.length - 1; i >= 0; i -= 1) {
      const column = pinnedInTableOrder[i];
      const width = column.getSize();
      offsets.set(column.id, { right: accumulated, width, isEdge: i === 0 });
      accumulated += width;
    }
    return offsets;
  }, [leafColumns, pinnedRightColumns]);

  const hasPinnedColumns = pinnedOffsets.size > 0;

  const cellPadding = density === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2.5';
  const headerPadding = density === 'compact' ? 'px-3 h-8' : 'px-3 h-9';

  // Track which horizontal edges have more content, so we can hint at
  // off-screen columns (the main discoverability gap in wide tables).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft < maxScroll - 1 });
  }, []);

  useEffect(() => {
    if (!scrollHint) return;
    const el = scrollRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener('scroll', updateEdges, { passive: true });
    const observer = new ResizeObserver(updateEdges);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      observer.disconnect();
    };
    // Re-measure when the rendered rows/columns change the content width.
  }, [scrollHint, updateEdges, data, columns, isLoading]);

  const wrapperStyle = maxHeight ? { maxHeight } : undefined;
  const wrapperClassName = maxHeight ? 'min-h-[16rem]' : undefined;

  // Loading skeleton
  if (isLoading) {
    return (
      <Table wrapperClassName={wrapperClassName} wrapperStyle={wrapperStyle}>
        <TableHeader
          className={cn(stickyHeader && 'sticky top-0 z-20')}
          style={stickyHeader ? { backgroundColor: 'hsl(var(--card))' } : undefined}
        >
          <TableRow>
            {leafColumns.map((column) => {
              const pinned = pinnedOffsets.get(column.id);
              return (
                <TableHead
                  key={column.id}
                  className={cn(
                    headerPadding,
                    pinned && 'sticky z-30 table-head-pinned',
                    pinned?.isEdge && 'table-cell-pinned-edge'
                  )}
                  style={
                    pinned
                      ? { right: pinned.right, width: pinned.width, minWidth: pinned.width }
                      : undefined
                  }
                >
                  <Skeleton className="h-3 w-16" />
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {leafColumns.map((column, colIndex) => {
                const pinned = pinnedOffsets.get(column.id);
                return (
                  <TableCell
                    key={column.id}
                    className={cn(
                      cellPadding,
                      pinned && 'sticky z-10 table-cell-pinned',
                      pinned?.isEdge && 'table-cell-pinned-edge'
                    )}
                    style={
                      pinned
                        ? { right: pinned.right, width: pinned.width, minWidth: pinned.width }
                        : undefined
                    }
                  >
                    <Skeleton
                      className={cn(
                        'h-3',
                        colIndex % 3 === 0 ? 'w-24' : colIndex % 3 === 1 ? 'w-16' : 'w-20'
                      )}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="relative">
      <Table wrapperRef={scrollRef} wrapperClassName={wrapperClassName} wrapperStyle={wrapperStyle}>
        <TableHeader
          className={cn(stickyHeader && 'sticky top-0 z-20')}
          style={stickyHeader ? { backgroundColor: 'hsl(var(--card))' } : undefined}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                const pinned = pinnedOffsets.get(header.column.id);

                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      headerPadding,
                      canSort && 'cursor-pointer select-none',
                      // Above the body's pinned cells (z-10) and the sticky
                      // header row (z-20), so it wins at the intersection.
                      pinned && 'sticky z-30 table-head-pinned',
                      pinned?.isEdge && 'table-cell-pinned-edge'
                    )}
                    style={
                      pinned
                        ? { right: pinned.right, width: pinned.width, minWidth: pinned.width }
                        : undefined
                    }
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <span className="flex flex-col">
                          <ChevronUp
                            className={cn(
                              'h-3 w-3 -mb-0.5',
                              sorted === 'asc' ? 'text-foreground' : 'text-muted-foreground/30'
                            )}
                          />
                          <ChevronDown
                            className={cn(
                              'h-3 w-3 -mt-0.5',
                              sorted === 'desc' ? 'text-foreground' : 'text-muted-foreground/30'
                            )}
                          />
                        </span>
                      )}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(onRowClick && 'cursor-pointer')}
                style={rowStyle?.(row.original)}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => {
                  const pinned = pinnedOffsets.get(cell.column.id);
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cellPadding,
                        pinned && 'sticky z-10 table-cell-pinned',
                        pinned?.isEdge && 'table-cell-pinned-edge'
                      )}
                      style={
                        pinned
                          ? { right: pinned.right, width: pinned.width, minWidth: pinned.width }
                          : undefined
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {scrollHint && edges.left && <ScrollEdge side="left" />}
      {/* Pinned columns already occupy the right edge — a fade there would just
          gray them out and imply hidden content that isn't there. */}
      {scrollHint && edges.right && !hasPinnedColumns && <ScrollEdge side="right" />}
    </div>
  );
}

/**
 * A pointer-transparent fade + chevron pinned to a horizontal edge of the
 * scroll viewport, cueing that more columns exist in that direction.
 */
function ScrollEdge({ side }: { side: 'left' | 'right' }) {
  const isLeft = side === 'left';
  const Chevron = isLeft ? ChevronLeft : ChevronRight;
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-y-0 z-10 flex w-10 items-center',
        isLeft ? 'left-0 justify-start' : 'right-0 justify-end'
      )}
      style={{
        background: `linear-gradient(to ${isLeft ? 'right' : 'left'}, hsl(var(--card)), transparent)`,
      }}
    >
      <Chevron className={cn('h-4 w-4 text-muted-foreground', isLeft ? 'ml-0.5' : 'mr-0.5')} />
    </div>
  );
}
